/**
 * FASE 9.2 — Fine Arts Wizard (Lloyd's Personal Collections / Chiodo a Chiodo)
 *
 * Pattern parallelo a CyberWizard:
 *  - schema-driven da GET /api/public/fine-arts/form-fields
 *  - conditional_on oggetto { field: "sez.key", value: true|false }
 *  - Array fields: valori_categorie.categorie_valori + storia_sinistri.sinistri_lista
 *  - IVASS step filtrato solo su gruppi Fine Arts + Colucci trasversali
 *  - Click-wrap art. 1892 c.c.
 *  - Submit POST /api/fine-arts/questionario
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { Loader2, Palette, AlertCircle } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import QuestionarioIntroBanner from "@/components/QuestionarioIntroBanner";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import { IvassConsentsStepUnified, registerConsentsBulk } from "@/components/wizard/IvassConsentsStepUnified";
import { ArrayField } from "@/components/wizard/ArrayField";
import { WizardDownloadPanel } from "@/components/wizard/WizardDownloadPanel";
import IVASSComplianceCheckboxes, { buildMupVisioneMeta, isIvassComplianceOk } from "@/components/IVASSComplianceCheckboxes";
import ConsensiGranulari, { buildConsensiGranulariMeta, isConsensiOk } from "@/components/ConsensiGranulari";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";
import AcceptAllInformative from "@/components/AcceptAllInformative";
import PrivacyFieldTooltip from "@/components/PrivacyFieldTooltip";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "fine_arts_draft_v1";
const IVASS_GROUPS = ["compagnia_lloyds_fine_arts", "colucci_trasversali"];

/* ---------- helpers ---------- */

// resolve conditional_on with object { field: "sez.key", value: true|false }
function resolveCondition(cond, formData) {
  if (!cond) return true;
  const path = (cond.field || "").split(".");
  if (path.length !== 2) return true;
  const [sez, key] = path;
  const actual = (formData[sez] || {})[key];
  return actual === cond.value;
}

/* ---------- field components ---------- */

function YesNoRadio({ tid, value, onChange }) {
  const isYes = value === true;
  const isNo = value === false;
  const base = "px-4 py-2 rounded-full text-sm font-medium transition-colors border";
  return (
    <div className="flex gap-2" data-testid={`${tid}-radio-group`}>
      <button type="button" data-testid={`${tid}-yes`}
              onClick={() => onChange(true)} aria-pressed={isYes}
              className={`${base} ${isYes
                ? "bg-[#163321] text-white border-[#163321]"
                : "bg-white text-[#163321] border-[#163321]/40 hover:bg-[#163321]/5"}`}>
        Sì
      </button>
      <button type="button" data-testid={`${tid}-no`}
              onClick={() => onChange(false)} aria-pressed={isNo}
              className={`${base} ${isNo
                ? "bg-[#7A1D1D] text-white border-[#7A1D1D]"
                : "bg-white text-[#7A1D1D] border-[#7A1D1D]/40 hover:bg-[#7A1D1D]/5"}`}>
        No
      </button>
    </div>
  );
}

function FieldInline({ sezKey, campoKey, campo, value, onChange }) {
  const tid = `fine-arts-field-${sezKey}-${campoKey}`;
  const v = value ?? "";
  if (campo.type === "boolean") {
    return <YesNoRadio tid={tid} value={value} onChange={onChange} />;
  }
  if (campo.type === "array" && campo.item_schema) {
    return <ArrayField label={null} value={Array.isArray(value) ? value : []}
                        itemSchema={campo.item_schema}
                        maxItems={campo.max_items || 5}
                        minItems={campo.min_items || 0}
                        onChange={onChange} testId={tid} />;
  }
  if (campo.type === "textarea") {
    return <Textarea data-testid={tid} value={v} rows={3}
                      onChange={(e) => onChange(e.target.value)}
                      className="rounded-xl border-[#E5E2DB] max-w-full" />;
  }
  if (campo.type === "select" && Array.isArray(campo.options)) {
    return (
      <Select value={v || ""} onValueChange={onChange}>
        <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid={tid}>
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          {campo.options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  const inputType = campo.type === "date" ? "date"
                  : campo.type === "email" ? "email"
                  : campo.type === "numeric" || campo.type === "currency" ? "number"
                  : "text";
  return (
    <div className="relative">
      {campo.type === "currency" && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51] pointer-events-none">€</span>
      )}
      <Input type={inputType} data-testid={tid} value={v}
              placeholder={campo.placeholder || ""}
              onChange={(e) => onChange(e.target.value)}
              className={`rounded-xl border-[#E5E2DB] h-11 ${campo.type === "currency" ? "pl-7" : ""} max-w-full`} />
    </div>
  );
}

function CampoRow({ sezKey, campoKey, campo, value, onChange }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5E2DB] hover:border-[#163321]/30 transition-colors">
      <div className="flex flex-col gap-2">
        <Label className="block text-sm text-[#0F1F14] font-medium leading-relaxed whitespace-pre-wrap break-words">
          {campo.label}
          {campo.required && <span className="text-red-600 ml-1">*</span>}
          {campo.help_text && (
            <span className="relative inline-block ml-1.5 group align-middle">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#D9C49A] text-[#163321] text-[10px] font-bold cursor-help"
                    data-testid={`fine-arts-help-${sezKey}-${campoKey}`}>?</span>
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-20 w-64 text-xs bg-[#163321] text-white p-2 rounded-lg shadow-lg leading-snug normal-case font-normal pointer-events-none">
                {campo.help_text}
              </span>
            </span>
          )}
        </Label>
        {campo.description && (
          <p className="text-xs text-gray-600 italic leading-relaxed">
            {campo.description}
          </p>
        )}
        <FieldInline sezKey={sezKey} campoKey={campoKey} campo={campo}
                      value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function SectionBlock({ sezKey, sez, formData, setField }) {
  const sectionData = formData[sezKey] || {};
  return (
    <div className="space-y-4" data-testid={`fine-arts-section-${sezKey}`}>
      <div>
        <h2 className="font-heading text-xl text-[#0F1F14] font-medium leading-snug">
          {sez.title || sez.label}
        </h2>
        {sez.subtitle && <p className="text-sm text-[#4A5E51]">{sez.subtitle}</p>}
      </div>
      <div className="space-y-3">
        {Object.entries(sez.campi || {}).map(([campoKey, campo]) => {
          if (!resolveCondition(campo.conditional_on, formData)) return null;
          return (
            <CampoRow key={campoKey}
                       sezKey={sezKey} campoKey={campoKey} campo={campo}
                       value={sectionData[campoKey]}
                       onChange={(v) => setField(sezKey, campoKey, v)} />
          );
        })}
      </div>
    </div>
  );
}

/* ---------- main wizard ---------- */

export default function FineArtsWizard() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [contact, setContact] = useState({
    client_name: "", client_email: "", client_telefono: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitErrors, setSubmitErrors] = useState(null);
  const [showPreExport, setShowPreExport] = useState(false);
  // FASE 13 — IVASS consents unificato: value cumulativo
  const [ivassBulk, setIvassBulk] = useState({ accepted: false, document_ids: [] });
  const ivassMandatoryOk = !!ivassBulk.accepted;
  const [signedDeclaration, setSignedDeclaration] = useState(false);
  // FIX 6 (v1.54.0.0) — MUP visione checkbox (audit compliance IVASS 40/2018)
  const [mupSeen, setMupSeen] = useState(false);
  const [mupFilename, setMupFilename] = useState(null);
  // v1.61 (Fase 2 Privacy) — Consensi granulari GDPR
  const [consensi, setConsensi] = useState({});
  // v1.72.3 — Cross-wizard: preferenze ricontatto + accept-all informative
  const [preferenzeRicontatto, setPreferenzeRicontatto] = useState({ canale: "", fascia: "" });
  const handleAcceptAllInformative = () => {
    setSignedDeclaration(true);
    setMupSeen(true);
    setConsensi((prev) => ({ ...prev, marketing: true, profilazione: true, gruppo_partner: true }));
  };
  const sessionHash = useMemo(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID() : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  []);

  // Load schema + localStorage draft
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/fine-arts/form-fields`);
        setSchema(data);
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const p = JSON.parse(raw);
            if (p.formData) setFormData(p.formData);
            if (p.contact) setContact(p.contact);
            if (p.currentStep) setCurrentStep(p.currentStep);
          }
        } catch (e) { /* ignore */ }
      } catch (e) {
        toast.error("Errore caricamento schema Fine Arts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // autosave
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(LS_KEY,
          JSON.stringify({ formData, contact, currentStep }));
      } catch (e) { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [formData, contact, currentStep]);

  const setField = useCallback((sez, key, value) => {
    setFormData((prev) => ({
      ...prev, [sez]: { ...(prev[sez] || {}), [key]: value },
    }));
  }, []);

  const orderedSteps = useMemo(() => {
    if (!schema?.wizard?.steps) return [];
    const steps = schema.wizard.steps.map((s) => ({
      ...s, sez: { ...schema.sezioni[s.key], key: s.key, title: s.title },
    }));
    steps.push({ key: "__ivass_compliance__", title: "Documenti IVASS",
                  sez: { key: "__ivass_compliance__", campi: {} }, virtual: true });
    return steps;
  }, [schema]);

  const openPreExport = () => {
    setSubmitErrors(null);
    setShowPreExport(true);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
  };

  const handleSubmit = async () => {
    setShowPreExport(false);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/fine-arts/questionario`, {
        form_data: formData,
        client_name: contact.client_name,
        client_email: contact.client_email,
        client_telefono: contact.client_telefono,
        signed_declaration: signedDeclaration,
        session_hash: sessionHash,
        mup_visione: buildMupVisioneMeta({ mupSeen, mupFilename }),
        consensi_granulari: buildConsensiGranulariMeta(consensi),
        // v1.72.3 — Preferenze ricontatto (canale + fascia oraria)
        preferenze_ricontatto: (preferenzeRicontatto.canale || preferenzeRicontatto.fascia)
          ? preferenzeRicontatto : null,
      });
      const submissionId = data.id || data.id_pratica;
      if (submissionId && ivassBulk.accepted && ivassBulk.document_ids.length) {
        try {
          await registerConsentsBulk({
            submissionId,
            documentIds: ivassBulk.document_ids,
            module: "fine_arts",
          });
        } catch (err) { /* audit non blocca */ }
      }
      setSubmitResult(data);
      setSubmitErrors(null);
      try { localStorage.removeItem(LS_KEY); } catch (e) { /* ignore */ }
      toast.success("Fine Arts inviato!");
    } catch (e) {
      // HOTFIX P0 — Il backend può ritornare:
      //   (a) HTTPException 422 custom: detail = {message, error_code, errors, missing_fields}
      //   (b) Pydantic 422 automatico: detail = [{loc, msg, type, ...}, ...]  (ARRAY!)
      // Se passi un array/oggetto a toast.error o React, crasha ("Objects are not
      // valid as a React child") → schermata bianca. Normalizziamo a stringa/array-of-string.
      const det = e.response?.data?.detail;
      let errs = [];
      let msg = "Errore invio";
      if (Array.isArray(det)) {
        // Pydantic 422 auto-format
        errs = det.map(d => {
          const loc = Array.isArray(d?.loc) ? d.loc.slice(1).join(".") : "";
          return `${loc ? loc + ": " : ""}${d?.msg || "campo non valido"}`;
        });
      } else if (det && typeof det === "object") {
        errs = Array.isArray(det.errors) ? det.errors : [];
        // missing_fields dal mapping validator
        if (!errs.length && Array.isArray(det.missing_fields)) {
          errs = det.missing_fields.map(f => f?.label || f?.key || "campo mancante");
        }
        msg = typeof det.message === "string" ? det.message : msg;
      } else if (typeof det === "string") {
        msg = det;
      }
      if (errs.length) {
        setSubmitErrors(errs);
        toast.error(`${errs.length} campi da correggere`);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (er) { /* ignore */ }
      } else {
        toast.error(msg);
      }
    } finally { setSubmitting(false); }
  };

  if (loading || !schema) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#163321]" />
        </main>
        <Footer />
      </>
    );
  }

  if (submitResult) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="fine-arts-thanks">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
            <WizardDownloadPanel
              title="Proposta Fine Arts generata!"
              downloads={submitResult.downloads || []}
              submissionId={submitResult.id}
              ramoLabel="Fine Arts Lloyd's"
              clientName={contact.client_name} />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalSteps = orderedSteps.length;
  const stepIdx = Math.max(0, currentStep - 1);
  const step = orderedSteps[stepIdx];
  const progress = ((stepIdx + 1) / totalSteps) * 100;

  return (
    <>
      <Header />
      <Toaster richColors position="top-center" />
      <main className="min-h-screen bg-[#FCFCFA] pt-20 overflow-x-hidden"
            data-testid="fine-arts-wizard-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Palette className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-2"
                data-testid="fine-arts-wizard-title">
              Questionario Fine Arts
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
              Lloyd&apos;s · Personal Collections / Chiodo a Chiodo · 10 sezioni
            </p>
          </div>

          <QuestionarioIntroBanner ramoRischio="Fine Arts" />

          {showPreExport && (
            <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6"
                  data-testid="fine-arts-wizard-pre-export-container">
              <h3 className="font-heading text-lg text-[#0F1F14] mb-3">
                Riepilogo prima dell&apos;invio
              </h3>
              <p className="text-sm text-[#4A5E51] mb-4">
                Verifica i dati inseriti. La proposta sarà generata sul template Lloyd&apos;s
                Fine Arts e trasmessa a Colucci Assicuratori per la valutazione.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <span className="text-xs text-[#7A8E83] block">Cliente</span>
                  <strong>{contact.client_name || "—"}</strong>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <span className="text-xs text-[#7A8E83] block">Email</span>
                  <strong>{contact.client_email || "—"}</strong>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <span className="text-xs text-[#7A8E83] block">Categorie di valore</span>
                  <strong>{((formData.valori_categorie || {}).categorie_valori || []).length}</strong>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <span className="text-xs text-[#7A8E83] block">Sinistri dichiarati</span>
                  <strong>{((formData.storia_sinistri || {}).sinistri_lista || []).length}</strong>
                </div>
              </div>
              {/* v1.72.3 — Preferenze ricontatto (canale + fascia) */}
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-3">Come preferisci essere ricontattato/a?</p>
                <ContattoPreferenzeBlock
                  value={preferenzeRicontatto}
                  onChange={setPreferenzeRicontatto}
                  testidPrefix="fine-arts-ricontatto"
                />
              </div>
              {/* v1.72.3 — Accetta tutte le informative facoltative */}
              <div className="mt-5">
                <AcceptAllInformative
                  onAcceptAll={handleAcceptAllInformative}
                  testid="fine-arts-accept-all-informative"
                />
              </div>
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]"
                    data-testid="fine-arts-signature-declaration">
                <IVASSComplianceCheckboxes
                  signedDeclaration={signedDeclaration}
                  onSignedDeclarationChange={setSignedDeclaration}
                  mupSeen={mupSeen}
                  onMupSeenChange={setMupSeen}
                  mupFilename={mupFilename}
                  onMupFilenameChange={setMupFilename}
                  testIdPrefix="fine-arts"
                />
              </div>
              {/* v1.61 — Consensi granulari GDPR */}
              <div className="mt-4">
                <ConsensiGranulari
                  value={consensi}
                  onChange={setConsensi}
                  testIdPrefix="fine-arts"
                  includeSanitario={false}
                />
              </div>
              <div className="mt-5 flex gap-3 justify-end">
                <button type="button"
                        onClick={() => setShowPreExport(false)}
                        data-testid="fine-arts-pre-export-back"
                        className="px-4 py-2 rounded-lg border border-[#163321]/30 text-[#163321] text-sm">
                  Torna indietro
                </button>
                <button type="button"
                        onClick={handleSubmit}
                        disabled={!isIvassComplianceOk({ signedDeclaration, mupSeen }) || !isConsensiOk(consensi) || submitting}
                        data-testid="fine-arts-pre-export-submit"
                        className="px-5 py-2 rounded-lg bg-[#163321] text-white text-sm font-medium
                                    disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting ? "Invio in corso…" : "Invia proposta"}
                </button>
              </div>
            </div>
          )}

          {!showPreExport && submitErrors && submitErrors.length > 0 && (
            <div className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5"
                  data-testid="fine-arts-submit-errors-banner"
                  role="alert" aria-live="polite">
              <h3 className="font-heading text-base text-red-800 font-medium mb-1 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                {submitErrors.length === 1
                  ? "1 campo da correggere prima di inviare"
                  : `${submitErrors.length} campi da correggere prima di inviare`}
              </h3>
              <ul className="space-y-1.5 mt-3">
                {submitErrors.map((e, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2"
                       data-testid={`fine-arts-submit-error-${i}`}>
                    <span className="text-red-400 mt-0.5" aria-hidden="true">•</span>
                    <span className="flex-1">{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <StepProgress
            steps={orderedSteps.map((s) => s.title)}
            currentStep={stepIdx}
            onStepClick={(i) => { setShowPreExport(false); setCurrentStep(i + 1); }}
            progress={progress}
            testIdPrefix="fine-arts"
          />

          <div className={showPreExport ? "hidden" : "bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6 max-w-full overflow-hidden"}>
            {stepIdx === 0 && (
              <div className="bg-[#FDF8E8] border border-[#D9C49A] rounded-2xl p-5 mb-5">
                <h3 className="font-heading text-base text-[#0F1F14] font-medium mb-3">
                  I tuoi contatti
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[["client_name", "Nome e cognome *", "text", "nome"],
                    ["client_email", "Email *", "email", "email"],
                    ["client_telefono", "Telefono", "tel", "telefono"]].map(([k, l, t, hint]) => (
                    <div key={k}>
                      <Label className="text-sm text-[#0F1F14] block mb-1.5">
                        {l}<PrivacyFieldTooltip finalita={hint} testIdPrefix={`fine-arts-${hint}`} />
                      </Label>
                      <Input type={t} data-testid={`fine-arts-contact-${k}`}
                              value={contact[k]}
                              onChange={(e) => setContact({ ...contact, [k]: e.target.value })}
                              className="rounded-xl border-[#E5E2DB] h-11 max-w-full" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step && step.key === "__ivass_compliance__" && (
              <IvassConsentsStepUnified
                module="fine_arts"
                groupFilter={IVASS_GROUPS}
                value={ivassBulk}
                onChange={setIvassBulk}
              />
            )}

            {step && step.key !== "__ivass_compliance__" && (
              <SectionBlock sezKey={step.key} sez={step.sez}
                             formData={formData} setField={setField} />
            )}
          </div>

          {!showPreExport && (
            <FormNavigation
              step={stepIdx} totalSteps={totalSteps}
              onPrev={() => setCurrentStep(Math.max(1, currentStep - 1))}
              onNext={() => setCurrentStep(Math.min(totalSteps, currentStep + 1))}
              onSubmit={openPreExport}
              submitting={submitting}
              canSubmit={ivassMandatoryOk}
              testIdPrefix="fine-arts"
            />
          )}
          {!showPreExport && !ivassMandatoryOk && stepIdx === totalSteps - 1 && (
            <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                data-testid="fine-arts-ivass-mandatory-warning">
              Devi accettare tutti i documenti obbligatori prima di poter inviare la proposta.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
